/**
 * Multi-page management. Add, remove, rename, duplicate, and switch pages.
 * Shared nav/footer live in state.sharedSections (single source of truth).
 * All mutations go through the state store for undo/redo support.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from '../editing/bridge.js';
import { hydratePage } from '../sections/section-manager.js';
import {
  PAGE_NAME_MAX_LEN,
  generatePageId,
  sanitizePageName,
  getUniquePageName
} from './page-name-utils.js';

let renderPreviewFn = null;
let showToastFn = null;
let showUndoToastFn = null;
let finishPendingEditFn = null;
let _switchSeq = 0;

export function init({ renderPreview, showToast, showUndoToast, finishPendingEdit }) {
  renderPreviewFn = renderPreview;
  showToastFn = showToast;
  showUndoToastFn = showUndoToast || null;
  finishPendingEditFn = typeof finishPendingEdit === 'function' ? finishPendingEdit : null;
}

/**
 * Add a new blank page and switch to it.
 * Uses direct state mutation + single history entry for atomicity.
 * @param {string} [name] - Page name (defaults to "Page N")
 * @returns {string} The new page's ID
 */
export async function addPage(name) {
  // BC-1: refuse BEFORE the splice. These page operations mutate live state
  // directly, so the store's guarded mutators never see them.
  if (store.refuseIfReadOnly('Add page')) return null;
  const state = store.getState();
  const baseDefault = `Page ${state.pages.length + 1}`;
  const { name: normalized } = sanitizePageName(name || baseDefault, baseDefault);
  const pageName = getUniquePageName(state, normalized);
  const newPage = {
    id: generatePageId(),
    name: pageName,
    sections: []
  };

  const insertIndex = state.pages.length;
  const oldActivePage = state.activePage;

  // Mutate state directly, then push a single compound history entry
  state.pages.splice(insertIndex, 0, structuredClone(newPage));
  state.activePage = newPage.id;

  const recorded = store.pushHistoryEntry(
    [
      { op: 'insert', path: 'pages', index: insertIndex, item: structuredClone(newPage) },
      { op: 'replace', path: 'activePage', value: newPage.id }
    ],
    [
      { op: 'replace', path: 'activePage', value: oldActivePage },
      { op: 'remove', path: 'pages', index: insertIndex }
    ],
    `Add page "${pageName}"`
  );
  // The store undid the splice (policy or writability). Nothing happened, so
  // nothing may be announced.
  if (recorded === false) return null;
  if (typeof store._bumpRevision === 'function') store._bumpRevision();
  store.notify();
  store._emitHistoryChanged();
  store.scheduleAutoSave();

  bridge.clearScroll();
  await renderPreviewFn({
    preserveScroll: false,
    transition: 'page-switch',
    hideDuringLoad: false
  });
  // PP-2: an add ends with a way back (the history entry above is what Undo
  // reverses); the undo IS the confirmation, never a second toast.
  if (showUndoToastFn) showUndoToastFn(`Added page "${pageName}"`, { id: 'undo-add-page' });
  else if (showToastFn) showToastFn(`Added page "${pageName}"`, 'success');
  return newPage.id;
}

/**
 * Remove a page by ID. Cannot remove the last remaining page.
 * If removing the active page, switches to the nearest sibling.
 * Single atomic history entry.
 * @param {string} pageId
 */
export async function removePage(pageId) {
  if (store.refuseIfReadOnly('Delete page')) return false;
  const state = store.getState();
  if (state.pages.length <= 1) {
    if (showToastFn) showToastFn('Cannot delete the last page', 'error');
    return false;
  }

  const idx = state.pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return false;

  const pageName = state.pages[idx].name;
  const wasActive = state.activePage === pageId;
  const removedPage = structuredClone(state.pages[idx]);
  const oldActivePage = state.activePage;

  // Mutate state
  state.pages.splice(idx, 1);

  let newActivePage = oldActivePage;
  if (wasActive) {
    const newActiveIdx = Math.min(idx, state.pages.length - 1);
    newActivePage = state.pages[newActiveIdx].id;
    state.activePage = newActivePage;
  }

  // Build atomic forward/reverse patches
  const forward = [{ op: 'remove', path: 'pages', index: idx }];
  const reverse = [{ op: 'insert', path: 'pages', index: idx, item: removedPage }];

  if (wasActive) {
    forward.push({ op: 'replace', path: 'activePage', value: newActivePage });
    reverse.unshift({ op: 'replace', path: 'activePage', value: oldActivePage });
  }

  if (store.pushHistoryEntry(forward, reverse, `Delete page "${pageName}"`) === false) return false;
  if (typeof store._bumpRevision === 'function') store._bumpRevision();
  store.notify();
  store._emitHistoryChanged();
  store.scheduleAutoSave();

  if (wasActive) {
    bridge.clearScroll();
    await renderPreviewFn({
      preserveScroll: false,
      transition: 'page-switch',
      hideDuringLoad: false
    });
  } else {
    await renderPreviewFn({ preserveScroll: true, transition: null, hideDuringLoad: false });
  }
  if (showUndoToastFn) showUndoToastFn(`Deleted page "${pageName}"`);
  else if (showToastFn) showToastFn(`Deleted page "${pageName}"`);
  return true;
}

/**
 * Rename a page.
 * @param {string} pageId
 * @param {string} newName
 */
export function renamePage(pageId, newName) {
  if (store.refuseIfReadOnly('Rename page')) return false;
  const state = store.getState();
  const idx = state.pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return false;

  const { name: normalized, empty, truncated } = sanitizePageName(newName, state.pages[idx].name);
  if (empty) {
    if (showToastFn) showToastFn('Page name cannot be empty', 'error');
    return false;
  }
  if (truncated && showToastFn) {
    showToastFn(`Page name limited to ${PAGE_NAME_MAX_LEN} characters`, 'info');
  }

  const oldName = state.pages[idx].name;
  const uniqueName = getUniquePageName(state, normalized, { excludePageId: pageId });
  if (oldName === uniqueName) return false;

  if (uniqueName !== normalized && showToastFn) {
    showToastFn(`Name already used. Renamed to "${uniqueName}"`, 'info');
  }

  // BC-1: the rename is announced only when the store actually took it.
  if (!store.update(`pages.${idx}.name`, uniqueName, `Rename page "${oldName}" to "${uniqueName}"`))
    return false;
  if (showToastFn) showToastFn(`Renamed page to "${uniqueName}"`);
  return true;
}

/**
 * Duplicate a page (deep-clone sections with new instanceIds).
 * Single atomic history entry.
 * @param {string} pageId
 * @returns {string|null} The new page's ID
 */
export async function duplicatePage(pageId) {
  if (store.refuseIfReadOnly('Duplicate page')) return null;
  const state = store.getState();
  const idx = state.pages.findIndex((p) => p.id === pageId);
  if (idx === -1) return null;

  const original = state.pages[idx];
  const newPage = structuredClone(original);
  newPage.id = generatePageId();
  const { name: copyBase } = sanitizePageName(
    `${original.name} (copy)`,
    `Page ${state.pages.length + 1}`
  );
  newPage.name = getUniquePageName(state, copyBase);

  // Assign fresh instanceIds to all sections
  for (const section of newPage.sections) {
    section.instanceId = 'inst-' + crypto.randomUUID().slice(0, 8);
    delete section.editableIdRepairs;
  }

  const insertIndex = idx + 1;
  const oldActivePage = state.activePage;

  // Mutate state
  state.pages.splice(insertIndex, 0, structuredClone(newPage));
  state.activePage = newPage.id;

  const recorded = store.pushHistoryEntry(
    [
      { op: 'insert', path: 'pages', index: insertIndex, item: structuredClone(newPage) },
      { op: 'replace', path: 'activePage', value: newPage.id }
    ],
    [
      { op: 'replace', path: 'activePage', value: oldActivePage },
      { op: 'remove', path: 'pages', index: insertIndex }
    ],
    `Duplicate page "${original.name}"`
  );
  if (recorded === false) return null;
  if (typeof store._bumpRevision === 'function') store._bumpRevision();
  store.notify();
  store._emitHistoryChanged();
  store.scheduleAutoSave();

  bridge.clearScroll();
  await renderPreviewFn({
    preserveScroll: false,
    transition: 'page-switch',
    hideDuringLoad: false
  });
  if (showToastFn) showToastFn(`Duplicated page "${original.name}"`, 'success');
  return newPage.id;
}

/**
 * Switch to a page by ID.
 * @param {string} pageId
 */
export async function setActivePage(pageId) {
  const state = store.getState();
  const target = state.pages.find((p) => p.id === pageId);
  if (!target) return;

  const mySeq = ++_switchSeq;
  if (state.activePage === pageId) return;

  // A page-tab click can beat the iframe blur/editing-complete message. Drain
  // and commit the text transaction before recording Switch page so one undo
  // never changes both content and activePage. The direct commit is a backstop
  // for non-text focus transactions and for test/legacy callers without a hook.
  if (finishPendingEditFn) await finishPendingEditFn();
  store.commitTransaction();

  if (target._pendingTemplateRefs?.length) {
    eventBus.emit('page:hydrating', { pageId, name: target.name });
    try {
      await hydratePage(pageId);
    } finally {
      eventBus.emit('page:hydrated', { pageId });
    }
    // A newer setActivePage call superseded us — let it win.
    if (mySeq !== _switchSeq) return;
  }

  store.update('activePage', pageId, 'Switch page');
  bridge.clearScroll();
  await renderPreviewFn({
    preserveScroll: false,
    transition: 'page-switch',
    hideDuringLoad: false
  });
}
